import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs, type Appointment, type Client, type Consultant, type ExecutionRow, type Paged, type ProjectDetail, type ProjectRow, type ProjectStatus } from '../api';
import { useMe } from '../auth';
import { DemoBadge, Dialog, Empty, ErrorAlert, Field, fieldError, Loading, Pending } from '../components/ui';
import { addDays, date, hm, localParts, MODALITY_LABEL, money, PROJECT_STATUS_LABEL, timeOf, todayIso, zonedIso } from '../format';
import { AppointmentItem } from './Agenda';
import { ExecStatus } from './Execucoes';
import { PhaseBadge, ProjectsViewToggle } from './Kanban';
import type { ProjectPhase } from '../api';

function StatusBadge({ status }: { status: ProjectStatus }) {
  return <span className={`badge ${status === 'ativo' ? 'badge-ok' : 'badge-off'}`}>{PROJECT_STATUS_LABEL[status]}</span>;
}

export function ProjectsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<'all' | 'linked'>('all');
  const [status, setStatus] = useState<ProjectStatus | 'all'>('ativo');
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [search, scope, status]);
  const q = useQuery({
    queryKey: ['projects', search, scope, status, page],
    queryFn: () => api.get<Paged<ProjectRow>>(`/projects${qs({ q: search, scope, status, page, pageSize: 30 })}`),
    placeholderData: (prev) => prev,
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Projetos</h1>
          <p>Todos os sócios acessam todos os projetos. O filtro “Meus” apenas organiza a lista.</p>
        </div>
        <div className="actions">
          <ProjectsViewToggle current="lista" />
          <Link className="btn btn-primary" to="/projetos/novo">
            Novo projeto
          </Link>
        </div>
      </div>
      <div className="toolbar">
        <label className="visually-hidden" htmlFor="p-search">Pesquisar</label>
        <input id="p-search" type="search" placeholder="Pesquisar por projeto, código ou cliente" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="segmented" role="group" aria-label="Escopo">
          <button aria-pressed={scope === 'all'} onClick={() => setScope('all')}>
            Todos
          </button>
          <button aria-pressed={scope === 'linked'} onClick={() => setScope('linked')}>
            Meus
          </button>
        </div>
        <label className="visually-hidden" htmlFor="p-status">Situação</label>
        <select id="p-status" value={status} onChange={(e) => setStatus(e.target.value as ProjectStatus | 'all')} style={{ flex: '0 1 180px' }}>
          <option value="all">Todas as situações</option>
          {Object.entries(PROJECT_STATUS_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </div>

      <ErrorAlert error={q.error} />
      {q.isLoading ? (
        <Loading />
      ) : q.data && q.data.data.length === 0 ? (
        <Empty>Nenhum projeto encontrado com esses filtros.</Empty>
      ) : (
        q.data && (
          <>
            <div className="card hide-mobile" style={{ padding: 0 }}>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Projeto</th>
                      <th>Cliente</th>
                      <th>Responsável</th>
                      <th>Fase</th>
                      <th>Situação</th>
                      <th>Início</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.data.map((p) => (
                      <tr key={p.id} className="clickable" onClick={() => navigate(`/projetos/${p.id}`)}>
                        <td>
                          <Link to={`/projetos/${p.id}`} onClick={(e) => e.stopPropagation()}>
                            {p.name}
                          </Link>{' '}
                          {p.linkedToMe && <span className="badge badge-brand">Meu</span>} <DemoBadge show={p.isDemo} />
                          <div className="small muted">{p.code}</div>
                        </td>
                        <td>{p.clientTradeName || p.clientName}</td>
                        <td>{p.ownerName}</td>
                        <td>
                          <PhaseBadge name={p.phaseName} color={p.phaseColor} />
                        </td>
                        <td>
                          <StatusBadge status={p.status} />
                        </td>
                        <td>{date(p.startDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="list show-mobile">
              {q.data.data.map((p) => (
                <Link key={p.id} to={`/projetos/${p.id}`} className="list-item">
                  <div className="title">
                    {p.name} {p.linkedToMe && <span className="badge badge-brand">Meu</span>}
                  </div>
                  <div className="meta">
                    <span>{p.clientTradeName || p.clientName}</span>
                    <span>Resp.: {p.ownerName}</span>
                    <PhaseBadge name={p.phaseName} color={p.phaseColor} />
                    <StatusBadge status={p.status} />
                  </div>
                </Link>
              ))}
            </div>
            {q.data.meta.totalPages > 1 && (
              <div className="actions" style={{ marginTop: '1rem', justifyContent: 'center' }}>
                <button className="btn btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Anterior
                </button>
                <span className="small muted" style={{ alignSelf: 'center' }}>
                  Página {page} de {q.data.meta.totalPages}
                </span>
                <button className="btn btn-sm" disabled={page >= q.data.meta.totalPages} onClick={() => setPage(page + 1)}>
                  Próxima
                </button>
              </div>
            )}
          </>
        )
      )}
    </>
  );
}

export function ProjectDetailPage() {
  const { id } = useParams();
  const me = useMe();
  const [ownerDialog, setOwnerDialog] = useState(false);
  const q = useQuery({ queryKey: ['project', id], queryFn: () => api.get<ProjectDetail>(`/projects/${id}`) });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorAlert error={q.error} />;
  const p = q.data!;

  return (
    <>
      <div className="page-head">
        <div>
          <p className="small">
            <Link to="/projetos">← Projetos</Link>
          </p>
          <h1>
            {p.name} <DemoBadge show={p.isDemo} />
          </h1>
          <p>
            {p.code} · <Link to={`/cadastros/clientes/${p.clientId}`}>{p.clientTradeName || p.clientName}</Link>
          </p>
        </div>
        <div className="actions">
          <Link className="btn" to={`/projetos/${p.id}/editar`}>
            Editar
          </Link>
          <button className="btn" onClick={() => setOwnerDialog(true)}>
            Trocar responsável
          </button>
        </div>
      </div>

      {!p.linkedToMe && (
        <div className="alert alert-info">
          Este projeto está vinculado a outro consultor ({p.ownerName}). Você pode consultá-lo e registrar normalmente, sem justificativa.
        </div>
      )}

      <div className="grid grid-2">
        <section className="card">
          <h2>Dados do projeto</h2>
          <dl className="props">
            <dt>Fase</dt>
            <dd>
              <ProjectPhaseControl project={p} />
            </dd>
            <dt>Situação</dt>
            <dd>
              <StatusBadge status={p.status} />
            </dd>
            <dt>Responsável habitual</dt>
            <dd>
              {p.ownerName}
              {p.ownerConsultantId === me.consultantId && ' (você)'}
            </dd>
            <dt>Participantes</dt>
            <dd>{p.participants.map((x) => x.name).join(', ')}</dd>
            <dt>Período</dt>
            <dd>
              {date(p.startDate)} a {date(p.endDate)}
            </dd>
            <dt>Escopo</dt>
            <dd style={{ whiteSpace: 'pre-wrap' }}>{p.scope || '—'}</dd>
            <dt>Observações</dt>
            <dd style={{ whiteSpace: 'pre-wrap' }}>{p.notes || '—'}</dd>
          </dl>
        </section>
        <section className="card">
          <div className="card-head">
            <h2>Contratos</h2>
            <Link className="btn btn-sm btn-primary" to={`/projetos/${p.id}/contratos/novo`}>
              Novo contrato
            </Link>
          </div>
          {p.contracts.length === 0 ? (
            <Empty>Nenhum contrato. Cadastre a condição comercial antes de registrar execuções cobradas.</Empty>
          ) : (
            <div className="list">
              {p.contracts.map((c) => (
                <Link key={c.id} className="list-item" to={`/contratos/${c.id}`}>
                  <div className="title">
                    {MODALITY_LABEL[c.modality]} <span className="badge">{c.code}</span>{' '}
                    {c.parentContractId && <span className="badge">Aditivo</span>}{' '}
                    <span className={`badge ${c.status === 'ativo' ? 'badge-ok' : 'badge-off'}`}>{c.status}</span>
                  </div>
                  <div className="meta">
                    <span>
                      Vigência: {date(c.startDate)} a {date(c.endDate)}
                    </span>
                    {c.hourlyRate && <span>Hora: {money(c.hourlyRate)}</span>}
                    {c.contractedMinutes != null && <span>Horas: {hm(c.contractedMinutes)}</span>}
                    {c.totalValue && <span>Total: {money(c.totalValue)}</span>}
                    {c.monthlyValue && <span>Mensal: {money(c.monthlyValue)}</span>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>

      {p.phaseHistory.length > 0 && (
        <section className="card">
          <h2>Histórico de fases</h2>
          <div className="list">
            {p.phaseHistory.map((h) => (
              <div key={h.id} className="list-item">
                <div className="title">
                  {h.fromName ? `${h.fromName} → ${h.toName ?? 'Sem fase'}` : `Início em ${h.toName ?? 'Sem fase'}`}
                </div>
                <div className="meta">
                  <span>{new Date(h.changedAt).toLocaleString('pt-BR')}</span>
                  {h.changedByName && <span>por {h.changedByName}</span>}
                  {h.note && <span>“{h.note}”</span>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="grid grid-2">
        <ProjectAgenda projectId={p.id} />
        <ProjectExecutions projectId={p.id} />
      </div>

      {ownerDialog && <ChangeOwnerDialog project={p} onClose={() => setOwnerDialog(false)} />}
    </>
  );
}

// Fase atual com troca direta (mesma regra do Kanban: grava histórico e respeita a versão).
function ProjectPhaseControl({ project }: { project: ProjectDetail }) {
  const qc = useQueryClient();
  const fases = useQuery({ queryKey: ['project-phases'], queryFn: () => api.get<{ data: ProjectPhase[] }>('/project-phases') });
  const mudar = useMutation({
    mutationFn: (phaseId: string) => api.post(`/projects/${project.id}/phase`, { phaseId, version: project.version }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', project.id] });
      qc.invalidateQueries({ queryKey: ['projects-kanban'] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
  return (
    <div className="actions" style={{ alignItems: 'center' }}>
      <PhaseBadge name={project.phase?.name} color={project.phase?.color} />
      <label className="visually-hidden" htmlFor="phase-change">
        Mudar fase
      </label>
      <select id="phase-change" className="kanban-mover" value="" disabled={mudar.isPending || !fases.data} onChange={(e) => e.target.value && mudar.mutate(e.target.value)}>
        <option value="">Mudar fase…</option>
        {(fases.data?.data ?? [])
          .filter((f) => f.active && f.id !== project.phaseId)
          .map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
      </select>
      <Link className="small" to="/projetos/kanban">
        Ver no Kanban
      </Link>
      {mudar.error ? <ErrorAlert error={mudar.error} title="Não foi possível mudar a fase." /> : null}
    </div>
  );
}

function ProjectAgenda({ projectId }: { projectId: string }) {
  const today = todayIso();
  const q = useQuery({
    queryKey: ['appointments', 'project', projectId],
    queryFn: () => api.get<{ data: Appointment[] }>(`/appointments${qs({ projectId, from: zonedIso(addDays(today, -14), '00:00'), to: zonedIso(addDays(today, 45), '00:00') })}`),
  });
  return (
    <section className="card">
      <div className="card-head">
        <h2>Agenda (−14 a +45 dias)</h2>
        <Link className="btn btn-sm" to="/agenda/novo">
          Novo compromisso
        </Link>
      </div>
      <ErrorAlert error={q.error} />
      {q.isLoading ? <Loading /> : q.data?.data.length === 0 ? <Empty>Nenhum compromisso no período.</Empty> : (
        <div className="list">
          {q.data?.data.map((a) => (
            <AppointmentItem key={a.id} a={{ ...a, title: `${date(localParts(a.startsAt).date).slice(0, 5)} · ${a.title}` }} />
          ))}
        </div>
      )}
    </section>
  );
}

function ProjectExecutions({ projectId }: { projectId: string }) {
  const q = useQuery({
    queryKey: ['executions', 'project', projectId],
    queryFn: () => api.get<Paged<ExecutionRow>>(`/executions${qs({ projectId, scope: 'all', pageSize: 20 })}`),
  });
  return (
    <section className="card">
      <div className="card-head">
        <h2>Execuções</h2>
        <Link className="btn btn-sm" to="/execucoes/nova">
          Nova execução
        </Link>
      </div>
      <ErrorAlert error={q.error} />
      {q.isLoading ? <Loading /> : q.data?.data.length === 0 ? <Empty>Nenhuma execução registrada.</Empty> : (
        <div className="list">
          {q.data?.data.map((e) => (
            <Link key={e.id} className="list-item" to={`/execucoes/${e.id}`}>
              <div className="title">
                {date(e.serviceDate)} · {hm(e.durationMinutes)} <ExecStatus status={e.status} />
              </div>
              <div className="meta">Executor: {e.executorName}</div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function ChangeOwnerDialog({ project, onClose }: { project: ProjectDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const consultants = useQuery({ queryKey: ['consultants'], queryFn: () => api.get<{ data: Consultant[] }>('/consultants') });
  const future = useQuery({
    queryKey: ['project-future', project.id],
    queryFn: () => api.get<{ data: Appointment[] }>(`/projects/${project.id}/future-appointments`),
  });
  const [newOwner, setNewOwner] = useState('');
  const [transfer, setTransfer] = useState<string[]>([]);
  const m = useMutation({
    mutationFn: () => api.post(`/projects/${project.id}/change-owner`, { newOwnerConsultantId: newOwner, version: project.version, transferAppointmentIds: transfer }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project', project.id] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['appointments'] });
      onClose();
    },
  });
  const items = future.data?.data ?? [];
  return (
    <Dialog title="Trocar responsável habitual" onClose={onClose}>
      <p>
        Mudança <strong>permanente</strong> do responsável. As execuções já registradas mantêm seus executores. Para uma substituição pontual,
        basta registrar a execução com outro executor — o responsável não muda.
      </p>
      <ErrorAlert error={m.error ?? future.error} />
      <Field label="Novo responsável" required>
        {(p) => (
          <select {...p} value={newOwner} onChange={(e) => setNewOwner(e.target.value)}>
            <option value="">Selecione…</option>
            {consultants.data?.data
              .filter((c) => c.active && c.id !== project.ownerConsultantId)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        )}
      </Field>
      <fieldset style={{ marginTop: '1rem' }}>
        <legend>Compromissos futuros de {project.ownerName} neste projeto</legend>
        {future.isLoading ? (
          <Loading />
        ) : items.length === 0 ? (
          <p className="small muted">Nenhum compromisso futuro a transferir.</p>
        ) : (
          <>
            <p className="small muted">Marque os que passam para o novo responsável. Os demais permanecem como estão.</p>
            {items.map((a) => (
              <label key={a.id} className="check">
                <input type="checkbox" checked={transfer.includes(a.id)} onChange={(e) => setTransfer((t) => (e.target.checked ? [...t, a.id] : t.filter((x) => x !== a.id)))} />
                {date(localParts(a.startsAt).date)} {timeOf(a.startsAt)} · {a.title}
              </label>
            ))}
          </>
        )}
      </fieldset>
      <div className="actions" style={{ marginTop: '1rem' }}>
        <button className="btn btn-primary" disabled={!newOwner || m.isPending} onClick={() => m.mutate()}>
          Confirmar troca{transfer.length ? ` e transferir ${transfer.length}` : ''}
        </button>
        <button className="btn" onClick={onClose}>
          Cancelar
        </button>
      </div>
    </Dialog>
  );
}

export function ProjectFormPage() {
  const { id } = useParams();
  const editing = Boolean(id);
  const me = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const existing = useQuery({ queryKey: ['project', id], queryFn: () => api.get<ProjectDetail>(`/projects/${id}`), enabled: editing });
  const clients = useQuery({ queryKey: ['clients', 'active-min'], queryFn: () => api.get<Paged<Client>>('/clients?active=true&pageSize=200') });
  const consultants = useQuery({ queryKey: ['consultants'], queryFn: () => api.get<{ data: Consultant[] }>('/consultants') });
  // fase inicial: vem do "+ Adicionar projeto" da lista do Kanban (?fase=) ou fica a primeira fase
  const [params] = useSearchParams();
  const phases = useQuery({ queryKey: ['project-phases'], queryFn: () => api.get<{ data: ProjectPhase[] }>('/project-phases'), enabled: !editing });
  const [phaseId, setPhaseId] = useState(params.get('fase') ?? '');

  const [form, setForm] = useState({
    code: '',
    clientId: '',
    name: '',
    scope: '',
    ownerConsultantId: me.consultantId,
    participantIds: [] as string[],
    startDate: '',
    endDate: '',
    status: 'ativo' as ProjectStatus,
    notes: '',
  });
  useEffect(() => {
    const p = existing.data;
    if (p)
      setForm({
        code: p.code,
        clientId: p.clientId,
        name: p.name,
        scope: p.scope ?? '',
        ownerConsultantId: p.ownerConsultantId,
        participantIds: p.participants.map((x) => x.id),
        startDate: p.startDate ?? '',
        endDate: p.endDate ?? '',
        status: p.status,
        notes: p.notes ?? '',
      });
  }, [existing.data]);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const m = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        code: form.code || undefined,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
      };
      if (editing) {
        await api.put(`/projects/${id}`, { ...body, version: existing.data!.version });
        return id!;
      }
      return (await api.post<{ id: string }>('/projects', { ...body, phaseId: phaseId || undefined })).id;
    },
    onSuccess: (pid) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['projects-kanban'] });
      void qc.invalidateQueries({ queryKey: ['project', pid] });
      void qc.invalidateQueries({ queryKey: ['me-overview'] });
      navigate(`/projetos/${pid}`);
    },
  });

  if (editing && existing.isLoading) return <Loading />;
  const err = m.error;

  function submit(e: FormEvent) {
    e.preventDefault();
    m.mutate();
  }

  return (
    <>
      <div className="page-head">
        <h1>{editing ? 'Editar projeto' : 'Novo projeto'}</h1>
      </div>
      <form className="card form" onSubmit={submit} noValidate style={{ maxWidth: 860 }}>
        <ErrorAlert error={err} />
        <div className="form-row">
          <Field label="Cliente" required error={fieldError(err, 'clientId')}>
            {(p) => (
              <select {...p} value={form.clientId} onChange={(e) => set('clientId', e.target.value)}>
                <option value="">Selecione…</option>
                {clients.data?.data.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.tradeName || c.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Código" hint={editing ? undefined : 'Deixe vazio para gerar automaticamente (PRJ-0001).'} error={fieldError(err, 'code')}>
            {(p) => <input {...p} value={form.code} onChange={(e) => set('code', e.target.value)} />}
          </Field>
        </div>
        <p className="small muted" style={{ marginTop: '-0.5rem' }}>
          Cliente não está na lista? <Link to="/cadastros/clientes/novo">Cadastre o cliente</Link> e volte.
        </p>
        <Field label="Nome do projeto" required error={fieldError(err, 'name')}>
          {(p) => <input {...p} value={form.name} onChange={(e) => set('name', e.target.value)} />}
        </Field>
        <div className="form-row">
          {editing ? (
            <Field label="Responsável habitual" hint="Para trocar, use “Trocar responsável” na página do projeto.">
              {(p) => <input {...p} value={existing.data?.ownerName ?? ''} disabled />}
            </Field>
          ) : (
            <Field label="Responsável habitual" required>
              {(p) => (
                <select {...p} value={form.ownerConsultantId} onChange={(e) => set('ownerConsultantId', e.target.value)}>
                  {consultants.data?.data
                    .filter((c) => c.active)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.id === me.consultantId ? ' (você)' : ''}
                      </option>
                    ))}
                </select>
              )}
            </Field>
          )}
          <Field label="Situação">
            {(p) => (
              <select {...p} value={form.status} onChange={(e) => set('status', e.target.value as ProjectStatus)}>
                {Object.entries(PROJECT_STATUS_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            )}
          </Field>
          {!editing && (
            <Field label="Fase inicial" hint="Coluna do Kanban em que o projeto começa.">
              {(p) => (
                <select {...p} value={phaseId} onChange={(e) => setPhaseId(e.target.value)}>
                  <option value="">Primeira fase ({phases.data?.data.find((f) => f.active)?.name ?? '…'})</option>
                  {(phases.data?.data ?? [])
                    .filter((f) => f.active)
                    .map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))}
                </select>
              )}
            </Field>
          )}
        </div>
        <fieldset>
          <legend>Participantes</legend>
          <div className="checks">
            {consultants.data?.data
              .filter((c) => c.active)
              .map((c) => {
                const isOwner = c.id === form.ownerConsultantId;
                return (
                  <label key={c.id} className="check">
                    <input
                      type="checkbox"
                      checked={isOwner || form.participantIds.includes(c.id)}
                      disabled={isOwner}
                      onChange={(e) =>
                        set('participantIds', e.target.checked ? [...form.participantIds, c.id] : form.participantIds.filter((x) => x !== c.id))
                      }
                    />
                    {c.name}
                    {isOwner && <span className="small muted">(responsável)</span>}
                  </label>
                );
              })}
          </div>
        </fieldset>
        <div className="form-row">
          <Field label="Início" error={fieldError(err, 'startDate')}>
            {(p) => <input {...p} type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />}
          </Field>
          <Field label="Término previsto" error={fieldError(err, 'endDate')}>
            {(p) => <input {...p} type="date" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />}
          </Field>
        </div>
        <Field label="Escopo">{(p) => <textarea {...p} value={form.scope} onChange={(e) => set('scope', e.target.value)} />}</Field>
        <Field label="Observações">{(p) => <textarea {...p} value={form.notes} onChange={(e) => set('notes', e.target.value)} />}</Field>
        <div className="actions">
          <button className="btn btn-primary" disabled={m.isPending || !form.clientId || !form.name}>
            {m.isPending ? 'Salvando…' : 'Salvar'}
          </button>
          <button type="button" className="btn" onClick={() => navigate(-1)}>
            Cancelar
          </button>
        </div>
      </form>
    </>
  );
}
